import React from "react";

import { PlayerType, Point } from "@/lib/types";

import { DEFAULT_HEATMAP_COLOR } from "./DataCard";

export interface Points {
  [color: string]: Point[];
}

interface Labels {
  [color: string]: string;
}

export interface BackgroundColors {
  outerTopLeft?: string;
  outerTopRight?: string;
  outerBottomLeft?: string;
  outerBottomRight?: string;
  innerTopLeft?: string;
  innerTopCenter?: string;
  innerTopRight?: string;
  innerMiddleLeft?: string;
  innerMiddleCenter?: string;
  innerMiddleRight?: string;
  innerBottomLeft?: string;
  innerBottomCenter?: string;
  innerBottomRight?: string;
}

export interface HeatMapChartProps {
  size?: string | number;
  outerTopLeft?: string;
  outerTopRight?: string;
  outerBottomLeft?: string;
  outerBottomRight?: string;
  innerTopLeft?: string;
  innerTopRight?: string;
  innerTopCenter?: string;
  innerBottomLeft?: string;
  innerBottomRight?: string;
  innerBottomCenter?: string;
  innerMiddleLeft?: string;
  innerMiddleRight?: string;
  innerMiddleCenter?: string;
  points?: Points;
  labels?: Labels;
  backgroundColors?: BackgroundColors;
  playerType: PlayerType;
}

const SVG_SIZE_IN_PX = 300;

const STRIKE_ZONE_BOTTOM_LEFT_IN_FEET = { x: -0.83, y: 1.52 };
const STRIKE_ZONE_TOP_RIGHT_IN_FEET = { x: 0.83, y: 3.73 };

const STRIKE_ZONE_BOTTOM_LEFT_IN_PX = { x: 49.5, y: 250.5 };
const STRIKE_ZONE_TOP_RIGHT_IN_PX = { x: 250.5, y: 49.5 };

const ZONE_EDGE_BOTTOM_LEFT_IN_PX = { x: 0, y: SVG_SIZE_IN_PX };
const ZONE_EDGE_TOP_RIGHT_IN_PX = { x: SVG_SIZE_IN_PX, y: 0 };

/**
 * Transforms backend coordinates to SVG coordinates.
 *
 * The backend uses a coordinate system where:
 * - X and Y range from -10 to 10
 * - Origin (0,0) is at the center
 * - Positive Y is up, negative Y is down
 *
 * The SVG viewBox uses:
 * - X and Y range from 0 to {SVG_SIZE}
 * - Origin (0,0) is at top-left corner
 * - Positive Y is down, negative Y is up (inverted from backend)
 *
 * @param x - X coordinate from backend (-10 to 10)
 * @param y - Y coordinate from backend (-10 to 10)
 * @returns SVG coordinates with x and y values (0 to {SVG_SIZE})
 */
function pointToStrikeZone(xInFeet: number, yInFeet: number) {
  const normalizedDistanceX =
    (xInFeet - STRIKE_ZONE_BOTTOM_LEFT_IN_FEET.x) /
    (STRIKE_ZONE_TOP_RIGHT_IN_FEET.x - STRIKE_ZONE_BOTTOM_LEFT_IN_FEET.x);

  const svgX =
    STRIKE_ZONE_BOTTOM_LEFT_IN_PX.x +
    normalizedDistanceX *
      (STRIKE_ZONE_TOP_RIGHT_IN_PX.x - STRIKE_ZONE_BOTTOM_LEFT_IN_PX.x);

  const normalizedDistanceY =
    (yInFeet - STRIKE_ZONE_BOTTOM_LEFT_IN_FEET.y) /
    (STRIKE_ZONE_TOP_RIGHT_IN_FEET.y - STRIKE_ZONE_BOTTOM_LEFT_IN_FEET.y);

  const svgY =
    STRIKE_ZONE_BOTTOM_LEFT_IN_PX.y -
    normalizedDistanceY *
      (STRIKE_ZONE_BOTTOM_LEFT_IN_PX.y - STRIKE_ZONE_TOP_RIGHT_IN_PX.y);

  return { x: svgX, y: svgY };
}

export function transformToSVG(
  xInFeet: number,
  yInFeet: number,
  playerType: PlayerType,
) {
  const point = pointToStrikeZone(
    playerType === "pitcher" ? xInFeet : -xInFeet, // mirror horizontal axis for hitters
    yInFeet,
  );

  return {
    x: Math.min(
      Math.max(point.x, ZONE_EDGE_BOTTOM_LEFT_IN_PX.x),
      ZONE_EDGE_TOP_RIGHT_IN_PX.x,
    ),
    y: Math.min(
      Math.max(point.y, ZONE_EDGE_TOP_RIGHT_IN_PX.y),
      ZONE_EDGE_BOTTOM_LEFT_IN_PX.y,
    ),
  };
}

const HeatMapChart: React.FC<HeatMapChartProps> = ({
  size,
  outerTopLeft,
  outerTopRight,
  outerBottomLeft,
  outerBottomRight,
  innerTopLeft,
  innerTopRight,
  innerTopCenter,
  innerBottomLeft,
  innerBottomRight,
  innerBottomCenter,
  innerMiddleLeft,
  innerMiddleRight,
  innerMiddleCenter,
  points = {},
  backgroundColors = {},
  playerType,
}) => (
  <svg
    width={size || SVG_SIZE_IN_PX}
    height={size || SVG_SIZE_IN_PX}
    viewBox={`0 0 ${SVG_SIZE_IN_PX} ${SVG_SIZE_IN_PX}`}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    // This is used to prevent corner circles from being cut off
    style={{ overflow: "visible" }}
  >
    <g>
      {/* Outer Corners */}
      <path
        d="M150 0H0V150H150V0Z"
        fill={backgroundColors.outerTopLeft || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {outerTopLeft && (
        <text
          data-testid="outerTopLeft"
          x="12"
          y="30.5455"
          fontSize="18"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {outerTopLeft}
        </text>
      )}

      <path
        d="M300 0H150V150H300V0Z"
        fill={backgroundColors.outerTopRight || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {outerTopRight && (
        <text
          data-testid="outerTopRight"
          x="250"
          y="30.5455"
          fontSize="18"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {outerTopRight}
        </text>
      )}

      <path
        d="M150 150H0V300H150V150Z"
        fill={backgroundColors.outerBottomLeft || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {outerBottomLeft && (
        <text
          data-testid="outerBottomLeft"
          x="12"
          y="283.545"
          fontSize="18"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {outerBottomLeft}
        </text>
      )}

      <path
        d="M300 150H150V300H300V150Z"
        fill={backgroundColors.outerBottomRight || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {outerBottomRight && (
        <text
          data-testid="outerBottomRight"
          x="250"
          y="283.545"
          fontSize="18"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {outerBottomRight}
        </text>
      )}

      {/* Center Grid (3x3) */}
      <path
        d="M116.667 50H50V116.667H116.667V50Z"
        fill={backgroundColors.innerTopLeft || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {innerTopLeft && (
        <text
          data-testid="innerTopLeft"
          x="70.1878"
          y="88"
          fontSize="16"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {innerTopLeft}
        </text>
      )}

      <path
        d="M183.333 50H116.667V116.667H183.333V50Z"
        fill={backgroundColors.innerTopCenter || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {innerTopCenter && (
        <text
          data-testid="innerTopCenter"
          x="136.485"
          y="88"
          fontSize="16"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {innerTopCenter}
        </text>
      )}

      <path
        d="M250 50H183.333V116.667H250V50Z"
        fill={backgroundColors.innerTopRight || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {innerTopRight && (
        <text
          data-testid="innerTopRight"
          x="201.682"
          y="88"
          fontSize="16"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {innerTopRight}
        </text>
      )}

      <path
        d="M116.667 116.667H50V183.333H116.667V116.667Z"
        fill={backgroundColors.innerMiddleLeft || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {innerMiddleLeft && (
        <text
          data-testid="innerMiddleLeft"
          x="70.1878"
          y="155"
          fontSize="16"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {innerMiddleLeft}
        </text>
      )}

      <path
        d="M183.333 116.667H116.667V183.333H183.333V116.667Z"
        fill={backgroundColors.innerMiddleCenter || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {innerMiddleCenter && (
        <text
          data-testid="innerMiddleCenter"
          x="136.485"
          y="155"
          fontSize="16"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {innerMiddleCenter}
        </text>
      )}

      <path
        d="M250 116.667H183.333V183.333H250V116.667Z"
        fill={backgroundColors.innerMiddleRight || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {innerMiddleRight && (
        <text
          data-testid="innerMiddleRight"
          x="201.983"
          y="155"
          fontSize="16"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {innerMiddleRight}
        </text>
      )}

      <path
        d="M116.667 183.333H50V250H116.667V183.333Z"
        fill={backgroundColors.innerBottomLeft || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {innerBottomLeft && (
        <text
          data-testid="innerBottomLeft"
          x="70.557"
          y="223"
          fontSize="16"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {innerBottomLeft}
        </text>
      )}

      <path
        d="M183.333 183.333H116.667V250H183.333V183.333Z"
        fill={backgroundColors.innerBottomCenter || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {innerBottomCenter && (
        <text
          data-testid="innerBottomCenter"
          x="136.485"
          y="223"
          fontSize="16"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {innerBottomCenter}
        </text>
      )}

      <path
        d="M250 183.333H183.333V250H250V183.333Z"
        fill={backgroundColors.innerBottomRight || "#c4cdf6"}
        stroke="black"
        strokeWidth="2"
      />
      {innerBottomRight && (
        <text
          data-testid="innerBottomRight"
          x="202.475"
          y="223"
          fontSize="16"
          fill={DEFAULT_HEATMAP_COLOR}
        >
          {innerBottomRight}
        </text>
      )}

      {/* Points */}
      {Object.entries(points).map(([color, dots]) =>
        dots.map((dot, index) => {
          const { x, y } = transformToSVG(dot.x, dot.y, playerType);
          return (
            <circle
              key={`${color}-${index}`}
              cx={x}
              cy={y}
              r="5"
              fill={color}
              data-testid={`${color}-${index}`}
            />
          );
        }),
      )}
    </g>
  </svg>
);

export default HeatMapChart;
